import { Link } from "react-router-dom";
import BackIcon from "@mui/icons-material/ArrowBackIos";
import { Tooltip, Button, Typography } from "@mui/material";
import { getBackButtonTooltip } from "../../utils/navUtils";
import {
  useGetPageFromPath,
  useVisibleProcesses,
} from "../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useNavigate } from "react-router";
import styled from "styled-components/macro";
import { NavTitle } from "./NavTitle";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";

export function UpOneLevelButton({
  isBackButtonVisible,
  pathnameArray,
  shouldGoBackNotUp,
  lastLocationPathStored,
  longPressProps,
}: {
  isBackButtonVisible: boolean;
  pathnameArray: string[];
  shouldGoBackNotUp: boolean;
  lastLocationPathStored: string;
  longPressProps: any;
}) {
  const {
    isSchedulePage,
    isReportsPage,
    isGroupTilesPage,
    isAllWorkSchedulesPage,
    isProcessTilesPage,
  } = useGetPageFromPath();
  const ckey = useCustomerKey();
  const processes = useVisibleProcesses();
  const { translate } = useFlightTranslation();
  const workScheduleId = processes[0]?.workScheduleConfig.id;
  const [workSchedules] = useWorkScheduleConfigJsonListAtom();
  const navigate = useNavigate();
  const showNavTitle = useShowNavTitle();

  // back button returns up one level
  // or if on settings/help pages top level,
  // use last location instead
  let upOneLevel = pathnameArray
    .slice(0, -1)
    .map(pathItem =>
      // grid page always goes back to dashboard page (to see the tiles)
      pathItem === "grid" ? "dashboard" : pathItem
    )
    .join("/");
  // If we are returning to the process group tile page, add a work schedule id
  if (upOneLevel.endsWith("dashboard") && processes && processes.length > 0) {
    upOneLevel = upOneLevel + "?workSchedule=" + workScheduleId;
  }
  // If we are returning to the work schedule tile page, add "/schedule"
  if (!isSchedulePage && upOneLevel.endsWith(ckey)) {
    upOneLevel = upOneLevel + "/schedule";
  }
  // If we are on schedules page, return to Customer Tiles page (home page)
  if (isSchedulePage) {
    upOneLevel = "/";
  }

  // If we are on group tiles page for customer with single work schedule, then back button should go to customer tiles page
  if (isGroupTilesPage && workSchedules.length === 1) {
    upOneLevel = "/";
  }

  // If viewing a report, go back to the reports listing page
  if (isReportsPage) {
    upOneLevel = `/${ckey}/reports?workSchedule=${workScheduleId}`;
  }

  if (isAllWorkSchedulesPage) {
    if (isProcessTilesPage) {
      upOneLevel = `/${ckey}/dashboard?workSchedule=all`;
    }
  }
  return (
    <StyledLink
      {...(shouldGoBackNotUp
        ? {
            onClick: () => {
              navigate(-1);
            },
          }
        : {
            to: upOneLevel,
          })}
      className={`backArrowAndNameLink ${isBackButtonVisible ? "" : "hidden"}`}
      {...longPressProps}
    >
      <Tooltip
        title={translate(
          `Back to ${getBackButtonTooltip({
            upOneLevel,
            lastLocationPathStored,
            shouldGoBackNotUp,
          })}`
        )}
        placement="top-start"
        PopperProps={{ className: "backButtonTooltip" }}
      >
        <Button className="btn backButtonWrapper">
          <BackIcon className="backArrow" />
          {showNavTitle ? (
            <Typography variant="h6" className={`name`}>
              <NavTitle />
            </Typography>
          ) : null}
        </Button>
      </Tooltip>
    </StyledLink>
  );
}

const StyledLink = styled(Link)`
  opacity: 1;
  transition: opacity 0.3s;
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;
export function useShowNavTitle() {
  const { isSettingsPage, isHelpPage } = useGetPageFromPath();
  return isSettingsPage || isHelpPage;
}
